import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';
import { calendarImport } from '../api';

// Keeps the iPhone's own calendar in step with the lessons imported from Playtomic (see routes/calendarImport.js).
// The server cannot reach a phone's calendar, so the app does it: adds new lessons, changes edited ones and removes
// cancelled or undone ones. It only ever deletes events it added itself, never the coach's own events.
//
// What the phone remembers (localStorage): for every imported lesson, which calendar event it became.
//   { [bookingId]: { eventId, sig, start, adopted } }
// `adopted` = an event that was already in the calendar (same title and time) and was reused instead of duplicated;
// those are left alone when the lesson is cancelled.

const FLAG = 'cpImportedToIphone';
const MAP = 'cpImportedMap';
const INFO = 'cpImportedInfo';
const MIN_GAP_MS = 2 * 60 * 1000;
let lastRun = 0;
let running = null;

export const isAvailable = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

const read = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch (e) { return fallback; }
};
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ } };

export const isEnabled = () => read(FLAG, false) === true;
export const getInfo = () => read(INFO, null); // { at, added, changed, removed, tracked }

// A step that never answers must fail with a message, not leave a button spinning forever
const step = (label, promise, ms) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} did not respond. Close and reopen the app, then try again.`)), ms))
]);

const signature = (l) => [l.title, new Date(l.start).getTime(), new Date(l.end).getTime(), l.location, l.description].join('|');
const titleStartKey = (title, startMs) => `${String(title || '').trim().toLowerCase()}|${startMs}`;

async function removeEntry(map, bookingId, stats) {
  const entry = map[bookingId];
  if (!entry) return;
  if (!entry.adopted && entry.eventId) {
    try { await step('Removing a lesson', CapacitorCalendar.deleteEvent({ id: entry.eventId }), 15000); } catch (e) { /* already gone */ }
  }
  delete map[bookingId];
  stats.removed += 1;
}

async function run() {
  const res = await step('The CoachPad server', calendarImport.getMine(), 30000);
  const { lessons, windowFrom, windowTo } = res.data;
  const from = new Date(windowFrom).getTime();
  const to = new Date(windowTo).getTime();
  const map = read(MAP, {});
  const stats = { added: 0, changed: 0, removed: 0 };

  // What is on the phone now, so a look-alike is reused and an event the coach deleted by hand is not brought back
  let onPhone = [];
  try {
    const { result } = await step('Reading the iPhone calendar', CapacitorCalendar.listEventsInRange({ from, to }), 30000);
    onPhone = result || [];
  } catch (e) { onPhone = []; }
  const presentIds = new Set(onPhone.map(e => e.id));
  const byTitleStart = new Map(onPhone.map(e => [titleStartKey(e.title, new Date(e.startDate).getTime()), e]));

  const serverIds = new Set(lessons.map(l => l.bookingId));

  for (const lesson of lessons) {
    try {
      const entry = map[lesson.bookingId];
      if (lesson.status === 'cancelled') { await removeEntry(map, lesson.bookingId, stats); continue; }

      const sig = signature(lesson);
      const startMs = new Date(lesson.start).getTime();
      const endMs = new Date(lesson.end).getTime();

      if (entry) {
        if (entry.sig === sig) continue;                       // nothing changed
        if (!presentIds.has(entry.eventId)) {                  // the coach removed it on the phone: leave it removed
          entry.sig = sig;
          continue;
        }
        if (entry.adopted) { entry.sig = sig; continue; }       // not ours to rewrite
        await step('Updating a lesson', CapacitorCalendar.modifyEvent({
          id: entry.eventId, title: lesson.title, startDate: startMs, endDate: endMs,
          location: lesson.location || undefined, description: lesson.description || undefined
        }), 15000);
        entry.sig = sig;
        entry.start = startMs;
        stats.changed += 1;
        continue;
      }

      const lookAlike = byTitleStart.get(titleStartKey(lesson.title, startMs));
      if (lookAlike) {
        map[lesson.bookingId] = { eventId: lookAlike.id, sig, start: startMs, adopted: true };
        continue;
      }
      const created = await step('Adding a lesson', CapacitorCalendar.createEvent({
        title: lesson.title, startDate: startMs, endDate: endMs,
        location: lesson.location || undefined, description: lesson.description || undefined
      }), 15000);
      if (created && created.id) {
        map[lesson.bookingId] = { eventId: created.id, sig, start: startMs, adopted: false };
        stats.added += 1;
      }
    } catch (e) { /* one lesson failing should not stop the rest; it is retried next time */ }
  }

  // Lessons that are gone from the server inside the window (an import was undone): remove them from the phone too
  for (const bookingId of Object.keys(map)) {
    const entry = map[bookingId];
    if (!serverIds.has(bookingId) && entry.start >= from && entry.start <= to) await removeEntry(map, bookingId, stats);
  }

  write(MAP, map);
  const info = { at: new Date().toISOString(), ...stats, tracked: Object.keys(map).length };
  write(INFO, info);
  lastRun = Date.now();
  return info;
}

export function syncNow() {
  if (!running) running = run().finally(() => { running = null; });
  return running;
}

// Quiet refresh (app opened / came back to the front). Never throws.
export async function syncIfDue(force = false) {
  if (!isAvailable() || !isEnabled()) return null;
  if (!force && Date.now() - lastRun < MIN_GAP_MS) return null;
  try { return await syncNow(); } catch (e) { return null; }
}

// Asks iOS for calendar access, turns the sync on, and does the first one
export async function connect() {
  const { result } = await step('The calendar permission request', CapacitorCalendar.requestFullCalendarAccess(), 60000);
  if (result !== 'granted') {
    throw new Error('Calendar access was not allowed. Turn it on in iPhone Settings → CoachPad → Calendars, then try again.');
  }
  write(FLAG, true);
  return syncNow();
}

// Turns it off. With removeLessons the events CoachPad added are taken off the phone's calendar as well.
export async function disable(removeLessons) {
  write(FLAG, false);
  if (removeLessons) {
    const map = read(MAP, {});
    const stats = { added: 0, changed: 0, removed: 0 };
    for (const bookingId of Object.keys(map)) await removeEntry(map, bookingId, stats);
    write(MAP, map);
    write(INFO, null);
    return stats;
  }
  return null;
}
